// Background360Night.jsx
import * as THREE from "three";
import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";

export default function Background360Night({ daySrc, nightSrc, nightOn }) {
  const dayTex = useTexture(daySrc);
  const nightTex = useTexture(nightSrc);

  const { gl, camera } = useThree();

  const bgScene = useMemo(() => new THREE.Scene(), []);
  const nightMatRef = useRef(null);
  const targetRef = useRef(0);

  useEffect(() => {
    targetRef.current = nightOn ? 1 : 0;
  }, [nightOn]);

  // IMPORTANT: keep autoClear OFF so R3F doesn't wipe our background
  useEffect(() => {
    const prev = gl.autoClear;
    gl.autoClear = false;
    return () => {
      gl.autoClear = prev;
    };
  }, [gl]);

  useEffect(() => {
    [dayTex, nightTex].forEach((t) => {
      if (!t) return;
      t.colorSpace = THREE.SRGBColorSpace;
      t.flipY = true;
      t.needsUpdate = true;
    });
  }, [dayTex, nightTex]);

  useEffect(() => {
    if (!dayTex || !nightTex) return;

    while (bgScene.children.length) bgScene.remove(bgScene.children[0]);

    const geo = new THREE.SphereGeometry(200, 64, 64);

    const dayMat = new THREE.MeshBasicMaterial({
      map: dayTex,
      side: THREE.BackSide,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });

    const nightMat = new THREE.MeshBasicMaterial({
      map: nightTex,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });

    const dayMesh = new THREE.Mesh(geo, dayMat);
    dayMesh.scale.set(-1, 1, 1); // fixes mirrored pano

    const nightMesh = new THREE.Mesh(geo, nightMat);
    nightMesh.scale.set(-1, 1, 1);

    nightMatRef.current = nightMat;

    bgScene.add(dayMesh);
    bgScene.add(nightMesh);

    return () => {
      geo.dispose();
      dayMat.dispose();
      nightMat.dispose();
    };
  }, [bgScene, dayTex, nightTex]);

  useFrame((state, delta) => {
    const mat = nightMatRef.current;
    if (mat) {
      const speed = 3.0;
      const k = 1 - Math.exp(-speed * delta);
      mat.opacity = THREE.MathUtils.lerp(mat.opacity, targetRef.current, k);
    }

    // Clear and draw background first
    gl.clear(true, true, true);
    gl.render(bgScene, camera);

    // Reset depth so your 3D scene renders on top
    gl.clearDepth();
  }, -100);

  return null;
}